// frontend/src/components/chat/ChatInterface.jsx - Fixed scrolling and height constraints
import React, { useState, useEffect, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Bot, User, ChevronDown } from 'lucide-react'
import MessageBubble from './MessageBubble'
import ChatInput from './ChatInput'
import TypingIndicator from './TypingIndicator'

const ChatInterface = ({ 
  messages = [], 
  onSendMessage, 
  isTyping = false,
  placeholder = "Type your message...",
  disabled = false,
  sessionActive = false
}) => {
  const messagesEndRef = useRef(null)
  const messagesContainerRef = useRef(null)
  const [isAtBottom, setIsAtBottom] = useState(true)
  const [showScrollButton, setShowScrollButton] = useState(false)

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
    setIsAtBottom(true)
    setShowScrollButton(false)
  }

  useEffect(() => {
    if (isAtBottom) {
      scrollToBottom()
    }
  }, [messages, isTyping, isAtBottom])

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target
    const isNearBottom = scrollHeight - scrollTop - clientHeight < 100
    const isScrollable = scrollHeight > clientHeight
    
    setIsAtBottom(isNearBottom)
    setShowScrollButton(isScrollable && !isNearBottom)
  }

  const handleSendMessage = (message) => {
    onSendMessage?.(message)
    setIsAtBottom(true)
    setShowScrollButton(false)
  }

  return (
    <div className="flex flex-col h-full bg-white rounded-lg border border-gray-200 shadow-sm">
      {/* Chat Header */}
      <div className="flex-shrink-0 flex items-center space-x-3 p-4 border-b border-gray-200 bg-gray-50 rounded-t-lg">
        <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
          <Bot className="h-5 w-5 text-primary-600" />
        </div>
        <div>
          <h3 className="font-medium text-gray-900">AI Assistant</h3>
          <p className="text-sm text-gray-500">
            {sessionActive ? 'Ready to browse with video' : 'Ready to help with web browsing'}
          </p>
        </div>
      </div>

      {/* Messages Area - Fixed height with internal scrolling */}
      <div className="flex-1 relative min-h-0" style={{ minHeight: '400px' }}>
        <div 
          ref={messagesContainerRef}
          className="absolute inset-0 overflow-y-auto p-4 space-y-4 scroll-smooth"
          onScroll={handleScroll}
          style={{ 
            scrollbarWidth: 'thin',
            scrollbarColor: '#cbd5e0 transparent'
          }}
        >
          {messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-gray-500 min-h-[300px]">
              <Bot className="h-12 w-12 mb-4 text-gray-300" />
              <p className="text-lg font-medium mb-2">Start a conversation</p>
              <p className="text-sm text-center max-w-sm">
                {sessionActive 
                  ? 'Tell me what to do on the website. I can click, scroll, fill forms, and more!'
                  : 'Ask me to browse websites, fill forms, or perform any web-based tasks'
                }
              </p>
            </div>
          ) : (
            <AnimatePresence>
              {messages.map((message, index) => (
                <motion.div
                  key={message.id || index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <MessageBubble message={message} />
                </motion.div>
              ))}
            </AnimatePresence>
          )}

          {isTyping && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <TypingIndicator />
            </motion.div>
          )}
          
          <div ref={messagesEndRef} />
        </div>

        {/* Scroll to bottom button */}
        {showScrollButton && (
          <motion.button
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            onClick={scrollToBottom}
            className="absolute bottom-4 right-4 p-3 bg-primary-600 text-white rounded-full shadow-lg hover:bg-primary-700 transition-colors z-10"
            title="Scroll to bottom"
          >
            <ChevronDown className="h-4 w-4" />
          </motion.button>
        )}

        {/* Fade overlay for better scroll indication */}
        {showScrollButton && (
          <div className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-white to-transparent pointer-events-none" />
        )}
      </div>

      {/* Chat Input - Fixed at bottom */}
      <div className="flex-shrink-0 border-t border-gray-200 p-4">
        <ChatInput
          onSendMessage={handleSendMessage}
          placeholder={placeholder}
          disabled={disabled || isTyping}
          sessionActive={sessionActive}
          maxLength={1000}
        />
      </div>
    </div>
  )
}

export default ChatInterface